import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { 
  Bell, 
  Package, 
  AlertTriangle, 
  MessageSquare, 
  DollarSign,
  CheckCircle,
  Clock
} from 'lucide-react';
import axios from 'axios';

const NotificationCenter = () => {
  const { user, token, API } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && token) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    const data = notification.data || {};
    const isAdmin = user?.is_admin;
    
    switch (notification.type) {
      // Order validation pending - admin goes to validation tab
      case 'order_pending_validation':
        navigate('/admin?tab=pending-validation');
        break;
      
      // Order validated / payment instructions - buyer goes to orders
      case 'order_validated':
        if (data.order_id) {
          navigate(`/buyer?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/buyer?tab=orders');
        }
        break;

      // Order rejected
      case 'order_rejected':
        if (data.order_id) {
          navigate(`/buyer?tab=cancelled&order=${data.order_id}`);
        } else {
          navigate('/buyer?tab=cancelled');
        }
        break;

      // Order status updates
      case 'order_status':
      case 'order_confirmed':
      case 'order_created':
      case 'order_completed':
        if (isAdmin && data.order_id) {
          navigate(`/admin?tab=orders&order=${data.order_id}`);
        } else if (data.order_id) {
          navigate(`/buyer?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/buyer?tab=orders');
        }
        break;

      // Order cancelled / expired
      case 'order_cancelled':
      case 'order_expired':
        if (data.order_id) {
          navigate(`/buyer?tab=cancelled&order=${data.order_id}`);
        } else {
          navigate('/buyer?tab=cancelled');
        }
        break;
      
      // Payment confirmed - buyer sees their order
      case 'payment_confirmed':
        if (data.order_id) {
          navigate(`/buyer?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/buyer?tab=orders');
        }
        break;

      // Payment released / received - seller
      case 'payment_released':
      case 'payment_received':
        if (data.order_id) {
          navigate(`/seller?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/seller?tab=orders');
        }
        break;

      // Payment failed
      case 'payment_failed':
        if (data.order_id) {
          navigate(`/buyer?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/buyer?tab=orders');
        }
        break;
      
      // New order for seller
      case 'new_order':
      case 'order_new':
        if (isAdmin && data.order_id) {
          navigate(`/admin?tab=orders&order=${data.order_id}`);
        } else if (data.order_id) {
          navigate(`/seller?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/seller?tab=orders');
        }
        break;
      
      // Order modified by buyer
      case 'order_modified':
        if (isAdmin && data.order_id) {
          navigate(`/admin?tab=orders&order=${data.order_id}`);
        } else if (data.order_id) {
          navigate(`/seller?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/seller?tab=orders');
        }
        break;

      // Dispute related
      case 'dispute_opened':
      case 'dispute_message':
        if (data.dispute_id && isAdmin) {
          navigate(`/admin?tab=disputes&dispute=${data.dispute_id}`);
        } else if (data.order_id) {
          navigate(`/buyer?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/buyer?tab=orders');
        }
        break;

      case 'dispute_resolved':
        if (data.order_id) {
          navigate(`/buyer?tab=history&order=${data.order_id}`);
        } else {
          navigate('/buyer?tab=history');
        }
        break;
      
      // Support ticket related
      case 'new_support_ticket':
      case 'support_reply':
        if (data.ticket_id && isAdmin) {
          navigate(`/admin?tab=support&ticket=${data.ticket_id}`);
        } else if (data.ticket_id) {
          navigate(`/messages?ticket=${data.ticket_id}`);
        } else {
          navigate('/messages');
        }
        break;

      case 'ticket_closed':
        if (data.ticket_id) {
          navigate(`/messages?ticket=${data.ticket_id}`);
        } else {
          navigate('/messages');
        }
        break;
      
      // Listing related
      case 'listing_approved':
      case 'listing_rejected':
        if (data.listing_id) {
          navigate(`/listings/${data.listing_id}`);
        } else {
          navigate('/seller?tab=flights');
        }
        break;

      case 'listing_modified':
        if (data.listing_id) {
          navigate(`/listings/${data.listing_id}`);
        } else {
          navigate('/buyer?tab=orders');
        }
        break;
      
      // Modification response
      case 'modification_accepted':
      case 'modification_rejected':
      case 'replacement_requested':
        if (data.order_id) {
          navigate(`/seller?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/seller?tab=orders');
        }
        break;
      
      // Refund related - admin
      case 'refund_requested':
      case 'refund_processed':
        if (isAdmin) {
          navigate('/admin?tab=disputes');
        } else if (data.order_id) {
          navigate(`/buyer?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/buyer?tab=orders');
        }
        break;

      // ── Exchange / Devises ──────────────────────────────────────────
      case 'exchange_order_new':
        // Admin sees the new order in the exchange section
        if (data.order_id) {
          navigate(`/admin?tab=exchange&order=${data.order_id}`);
        } else {
          navigate('/admin?tab=exchange');
        }
        break;

      case 'exchange_order_validated':
      case 'exchange_payment_confirmed':
      case 'exchange_order_expiring':
        if (data.order_id) {
          navigate(`/currency/transactions?order=${data.order_id}`);
        } else {
          navigate('/currency/transactions');
        }
        break;

      case 'exchange_order_completed':
        if (data.order_id) {
          navigate(`/currency/transactions?order=${data.order_id}`);
        } else {
          navigate('/currency/transactions');
        }
        break;

      case 'exchange_order_cancelled':
      case 'exchange_order_rejected':
        if (data.order_id) {
          navigate(`/currency/transactions?order=${data.order_id}`);
        } else {
          navigate('/currency/transactions');
        }
        break;

      // Admin platform management
      case 'new_user_registered':
        navigate('/admin?tab=users');
        break;

      case 'new_listing_created':
        if (data.listing_id) {
          navigate(`/admin?tab=listings&listing=${data.listing_id}`);
        } else {
          navigate('/admin?tab=listings');
        }
        break;

      case 'exchange_payment_received':
        if (data.order_id) {
          navigate(`/admin?tab=exchange&order=${data.order_id}`);
        } else {
          navigate('/admin?tab=exchange');
        }
        break;

      case 'delivery_confirmed':
        if (data.order_id) {
          navigate(`/admin?tab=orders&order=${data.order_id}`);
        } else {
          navigate('/admin?tab=orders');
        }
        break;
      
      default:
        if (data.order_id) {
          navigate(`/buyer?tab=orders&order=${data.order_id}`);
        } else if (data.listing_id) {
          navigate(`/listings/${data.listing_id}`);
        } else if (data.ticket_id) {
          navigate(`/messages?ticket=${data.ticket_id}`);
        } else {
          navigate('/seller');
        }
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order_status':
      case 'order_confirmed':
      case 'order_completed':
      case 'order_created':
      case 'order_validated':
      case 'new_order':
        return <Package className="h-4 w-4 text-primary" />;
      case 'order_pending_validation':
        return <Clock className="h-4 w-4 text-amber-500" />;
      case 'order_cancelled':
      case 'order_expired':
      case 'order_rejected':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'payment_released':
      case 'payment_received':
      case 'payment_confirmed':
        return <DollarSign className="h-4 w-4 text-green-500" />;
      case 'payment_failed':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'exchange_order_new':
      case 'exchange_order_validated':
      case 'exchange_payment_confirmed':
      case 'exchange_order_completed':
        return <DollarSign className="h-4 w-4 text-[#D4AF37]" />;
      case 'exchange_order_expiring':
        return <Clock className="h-4 w-4 text-orange-400" />;
      case 'exchange_order_cancelled':
      case 'exchange_order_rejected':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'new_user_registered':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'new_listing_created':
        return <Package className="h-4 w-4 text-blue-500" />;
      case 'exchange_payment_received':
        return <DollarSign className="h-4 w-4 text-emerald-500" />;
      case 'delivery_confirmed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'dispute_opened':
      case 'dispute_message':
      case 'dispute_resolved':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'listing_approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'listing_rejected':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'listing_modified':
        return <Package className="h-4 w-4 text-amber-500" />;
      case 'new_support_ticket':
      case 'support_reply':
      case 'ticket_closed':
        return <MessageSquare className="h-4 w-4 text-violet-500" />;
      case 'refund_requested':
      case 'refund_processed':
        return <DollarSign className="h-4 w-4 text-amber-500" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes}min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR');
  };

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="rounded-full relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notification-dropdown">
        <div className="px-4 py-2 border-b">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Notifications</h4>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
        
        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <div className="py-8 text-center">
              <Bell className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
              <p className="text-sm text-muted-foreground">Aucune notification</p>
            </div>
          ) : (
            notifications.slice(0, 10).map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`flex items-start gap-3 p-3 cursor-pointer ${
                  !notification.read ? 'bg-primary/5' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
                data-testid={`notification-item-${notification.id}`}
              >
                <div className="mt-0.5">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!notification.read ? 'font-medium' : ''}`}>
                    {notification.title}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {notification.message}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatTime(notification.created_at)}
                  </p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
        
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Button 
                variant="ghost" 
                className="w-full text-sm"
                onClick={() => navigate('/notifications')}
              >
                Voir toutes les notifications
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationCenter;
